import React, { FC, useState, useMemo } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import { Typography, Button, Flex, Divider, Spin, Result, Tooltip } from 'antd'
import { DownOutlined, ExportOutlined } from '@ant-design/icons'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis, Legend } from 'recharts'
import { ReviewItem } from '../types'
import { PLATFORM_CONFIG, PLATFORM_ICONS } from '../utils'
import { useListingReviewsQuery } from './useListingReviewsQuery'
import { useListingReviewChartQuery } from './useListingReviewChartQuery'
import { REVIEWS_PAGE_SIZE } from './reviewQueryUtils'
import type { ListingRow } from '../Listings/Listings'

export interface ReviewChartEntry {
  month: string
  [platformKey: string]: string | number
}

interface ReviewPlatform {
  key: string
  label: string
  color: string
  activeBackground: string
}

const ALL_PLATFORMS: ReviewPlatform[] = Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => ({
  key,
  ...cfg,
}))

const { Text } = Typography

const SECTION_PADDING: React.CSSProperties = { padding: '0 24px' }

const formatReviewDate = (dateStr: string): string => moment(dateStr).format('YYYY-MM-DD')
const formatChartMonth = (value: string): string => moment(value, 'YYYY-MM').format('MMM YY')

/* ── Section Components ── */

const openListingButton = (url: string) => (
  <Tooltip title='Open listing'>
    <a href={url} target='_blank' rel='noreferrer' style={{ display: 'inline-flex', color: '#1890FF' }}>
      <ExportOutlined style={{ fontSize: 14 }} />
    </a>
  </Tooltip>
)

const TitleSection: FC<{ listing: ListingRow }> = ({ listing }) => {
  const linkByTitle = !listing.address && listing.url
  return (
    <div style={{ ...SECTION_PADDING, paddingTop: 20 }}>
      <Flex align='center' gap={8} wrap='wrap'>
        <div style={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: 18 }}>
          {`Reviews \u2014 Listing ${listing.listingId}`}
        </div>
        {linkByTitle && openListingButton(listing.url)}
      </Flex>
      {listing.address && (
        <Flex align='center' gap={6} style={{ marginTop: 4 }}>
          <span style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: 13, color: '#8c8c8c' }}>{listing.address}</span>
          {listing.url && !linkByTitle && openListingButton(listing.url)}
        </Flex>
      )}
    </div>
  )
}

interface FilterBarProps {
  platformCounts: Record<string, number>
  totalCount: number
  selectedPlatform: string | null
  onSelect: (platform: string | null) => void
}

const FilterBar: FC<FilterBarProps> = ({ platformCounts, totalCount, selectedPlatform, onSelect }) => {
  const activePlatforms = ALL_PLATFORMS.filter(p => (platformCounts[p.key] || 0) > 0)
  const singlePlatform = activePlatforms.length === 1

  return (
    <Flex gap={8} wrap='wrap' style={SECTION_PADDING}>
      {!singlePlatform && (
        <Button type={selectedPlatform === null ? 'primary' : 'default'} onClick={() => onSelect(null)}>
          All ({totalCount})
        </Button>
      )}
      {activePlatforms.map(p => {
        const count = platformCounts[p.key]
        const isActive = singlePlatform || selectedPlatform === p.key
        return (
          <Button
            key={p.key}
            type='default'
            onClick={singlePlatform ? undefined : () => onSelect(p.key)}
            style={{
              borderColor: isActive ? p.color : undefined,
              background: isActive ? p.activeBackground : undefined,
              cursor: singlePlatform ? 'default' : undefined,
            }}
          >
            <Flex align='center' gap={6}>
              {PLATFORM_ICONS[p.key] && (
                <img src={PLATFORM_ICONS[p.key]} alt={p.label} style={{ width: 16, height: 16 }} />
              )}
              <span>
                {p.label} ({count})
              </span>
            </Flex>
          </Button>
        )
      })}
    </Flex>
  )
}

interface StaysTimelineProps {
  chartData: ReviewChartEntry[]
  selectedPlatform: string | null
  activePlatforms: ReviewPlatform[]
}

const StaysTimeline: FC<StaysTimelineProps> = ({ chartData, selectedPlatform, activePlatforms }) => (
  <div style={SECTION_PADDING}>
    <Text style={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: 14, display: 'block', marginBottom: 12 }}>
      Stays Timeline
    </Text>
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={chartData} margin={{ left: -20 }}>
          <CartesianGrid vertical={false} stroke='#f0f0f0' />
          <XAxis
            dataKey='month'
            tickFormatter={formatChartMonth}
            tickLine={false}
            axisLine={{ stroke: '#f0f0f0' }}
            tick={{ fontSize: 10, fontFamily: 'Source Sans Pro' }}
            interval='preserveStartEnd'
            angle={-45}
            textAnchor='end'
            height={45}
          />
          <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fontFamily: 'Source Sans Pro' }} />
          <RechartsTooltip cursor={false} contentStyle={{ fontFamily: 'Source Sans Pro' }} labelFormatter={formatChartMonth} />
          <Legend wrapperStyle={{ display: 'none' }} />
          {activePlatforms.map((p, index) => (
            <Bar
              key={p.key}
              dataKey={p.key}
              stackId='stack'
              fill={p.color}
              radius={index === activePlatforms.length - 1 ? [1, 1, 0, 0] : [0, 0, 0, 0]}
              barSize={24}
              name={p.label}
              hide={selectedPlatform !== null && selectedPlatform !== p.key}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
)

interface ReviewsListProps {
  reviews: ReviewItem[]
  total: number
  hasNextPage: boolean
  isFetching: boolean
  onLoadMore: () => void
}

const ReviewsList: FC<ReviewsListProps> = ({ reviews, total, hasNextPage, isFetching, onLoadMore }) => (
  <div style={{ paddingBottom: 20 }}>
    <Text
      style={{
        ...SECTION_PADDING,
        fontFamily: 'Roboto',
        fontWeight: 500,
        fontSize: 14,
        display: 'block',
        marginBottom: 12,
      }}
    >
      Reviews ({total})
    </Text>
    <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 8 }}>
      {reviews.map((review, index) => (
        <div
          key={`${review.id}-${index}`}
          style={{ padding: '12px 24px 12px 24px', borderBottom: '1px solid #f0f0f0' }}
        >
          <Flex align='center' justify='space-between' style={{ marginBottom: 6 }}>
            <Flex align='center' gap={8}>
              {PLATFORM_ICONS[review.activitySource] && (
                <img
                  src={PLATFORM_ICONS[review.activitySource]}
                  alt={review.activitySource}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
              )}
              <Text strong style={{ fontSize: 13 }}>
                {review.activityUserName}
              </Text>
            </Flex>

            <Text type='secondary' style={{ fontSize: 12 }}>
              {formatReviewDate(review.activityDate)}
            </Text>
          </Flex>
          <Text style={{ fontSize: 13, lineHeight: '20px', paddingLeft: 24, display: 'block' }}>
            {review.activityText}
          </Text>
        </div>
      ))}
      {hasNextPage && (
        <Flex justify='center' style={{ padding: 16 }}>
          <Button type='text' icon={<DownOutlined />} loading={isFetching} onClick={onLoadMore}>
            Load More
          </Button>
        </Flex>
      )}
    </div>
  </div>
)

interface ReviewDetailModalProps {
  listing: ListingRow
  activityIndex: string
}

const ReviewDetailModal: FC<ReviewDetailModalProps> = ({ listing, activityIndex }) => {
  const { entityId } = useParams<{ entityId: string }>()
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)

  const {
    chartData,
    platformCounts,
    total: totalReviewCount,
    isLoading: isChartLoading,
    isError: isChartError,
  } = useListingReviewChartQuery({
    entityId: entityId || '',
    listingId: listing.listingId,
    activityIndex,
  })

  const {
    reviews,
    total: reviewsTotal,
    isLoading: isReviewsLoading,
    isFetching: isReviewsFetching,
    isError: isReviewsError,
    fetchNextPage,
    hasNextPage,
  } = useListingReviewsQuery({
    entityId: entityId || '',
    listingId: listing.listingId,
    activityIndex,
    selectedPlatform,
    pageSize: REVIEWS_PAGE_SIZE,
  })

  const activePlatforms = useMemo(() => ALL_PLATFORMS.filter(p => (platformCounts[p.key] || 0) > 0), [platformCounts])

  const isLoading = isChartLoading || isReviewsLoading
  const isError = isChartError || isReviewsError

  if (isLoading) {
    return (
      <Flex vertical gap={8} justify='center' align='center' style={{ padding: 60 }}>
        <Spin />
        <Text type='secondary'>Loading...</Text>
      </Flex>
    )
  }

  if (isError) {
    return <Result status='error' title='Failed to load reviews' subTitle='Please try again later.' />
  }

  if (totalReviewCount === 0 && reviews.length === 0) {
    return (
      <Flex justify='center' align='center' style={{ padding: 40 }}>
        <Text type='secondary'>No review details available for this listing.</Text>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <TitleSection listing={listing} />

      <Divider style={{ margin: 0 }} />

      <FilterBar
        platformCounts={platformCounts}
        totalCount={totalReviewCount}
        selectedPlatform={selectedPlatform}
        onSelect={setSelectedPlatform}
      />

      <Divider style={{ margin: 0 }} />

      <StaysTimeline chartData={chartData} selectedPlatform={selectedPlatform} activePlatforms={activePlatforms} />

      <Divider style={{ margin: 0 }} />

      <ReviewsList
        reviews={reviews}
        total={reviewsTotal}
        hasNextPage={hasNextPage}
        isFetching={isReviewsFetching}
        onLoadMore={fetchNextPage}
      />
    </Flex>
  )
}

export default ReviewDetailModal
